/**
 * [CLAIMS_CONTEXT]
 * purpose: Provides configuration for glass damage modal sections per damage type.
 * touches: setup, behavior, glass damage flow
 * invariants: defaults to all sections if no custom config exists
 * inputs: selfCareSettings from store, glass damage type
 * outputs: list of enabled sections for the modal
 * see: frontend/src/shared/insurer.ts, frontend/src/declaration/components/glassDamage/GlassDamageModal.vue, frontend/src/declaration/helpers/glassDamageModalConfig.ts
 */
import { GlassDamageModalSection } from '@shared/insurer';
/**
 * Default sections shown for all glass damage types if no custom configuration exists.
 * This represents the full flow: description, glazing type, glass type, and pictures.
 */
const DEFAULT_SECTIONS = [
    GlassDamageModalSection.DamageDescription,
    GlassDamageModalSection.GlazingType,
    GlassDamageModalSection.GlassType,
    GlassDamageModalSection.Pictures,
];
/**
 * Retrieves the enabled sections for a specific glass damage type from selfCareSettings.
 * If no custom configuration exists for the given type, returns all sections by default.
 *
 * @param damageType - The glass damage type (e.g., ShowerWall, Window)
 * @param selfCareSettings - The settings from the store containing insurer configuration
 * @returns Array of enabled modal sections for this damage type
 *
 * @example
 * // In selfCareSettings from store:
 * // {
 * //   declaration: {
 * //     pages: {
 * //       glassDamagesQuestion: {
 * //         GlassDamageModal: {
 * //           enabledSections: {
 * //             'paroi-de-douche': [GlassDamageModalSection.Pictures]
 * //           }
 * //         }
 * //       }
 * //     }
 * //   }
 * // }
 * const sections = getEnabledSectionsForDamageType(GlassDamageType.ShowerWall, selfCareSettings)
 * // Returns: [GlassDamageModalSection.Pictures]
 */
export function getEnabledSectionsForDamageType(damageType, selfCareSettings) {
    var _a, _b, _c;
    if (!damageType) {
        return DEFAULT_SECTIONS;
    }
    const config = (_c = (_b = (_a = selfCareSettings === null || selfCareSettings === void 0 ? void 0 : selfCareSettings.declaration) === null || _a === void 0 ? void 0 : _a.pages) === null || _b === void 0 ? void 0 : _b.glassDamagesQuestion) === null || _c === void 0 ? void 0 : _c.GlassDamageModal;
    if (!(config === null || config === void 0 ? void 0 : config.enabledSections)) {
        return DEFAULT_SECTIONS;
    }
    // The config uses the enum values as keys (e.g., 'paroi-de-douche')
    // damageType is already the enum value, so we can use it directly
    const customSections = config.enabledSections[damageType];
    const result = customSections && customSections.length > 0 ? customSections : DEFAULT_SECTIONS;
    return result;
}
/**
 * Checks if a specific section is enabled for a given damage type.
 *
 * @param damageType - The glass damage type
 * @param section - The section to check
 * @param selfCareSettings - The settings from the store containing insurer configuration
 * @returns True if the section is enabled, false otherwise
 */
export function isSectionEnabled(damageType, section, selfCareSettings) {
    const enabledSections = getEnabledSectionsForDamageType(damageType, selfCareSettings);
    return enabledSections.includes(section);
}
/**
 * Checks if photos are required for a given damage type.
 *
 * @param damageType - The glass damage type
 * @param selfCareSettings - The settings from the store containing insurer configuration
 * @returns True if photos are required, false otherwise
 */
export function arePhotosRequired(damageType, selfCareSettings) {
    var _a, _b, _c, _d;
    if (!damageType) {
        return false;
    }
    const config = (_c = (_b = (_a = selfCareSettings === null || selfCareSettings === void 0 ? void 0 : selfCareSettings.declaration) === null || _a === void 0 ? void 0 : _a.pages) === null || _b === void 0 ? void 0 : _b.glassDamagesQuestion) === null || _c === void 0 ? void 0 : _c.GlassDamageModal;
    if (!(config === null || config === void 0 ? void 0 : config.requiredPhotos)) {
        return false;
    }
    return (_d = config.requiredPhotos[damageType]) !== null && _d !== void 0 ? _d : false;
}
